import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Input, Form, Upload } from "antd";
import { IoIosSearch } from "react-icons/io";
import { FilterMatchMode } from "primereact/api";
import { UploadOutlined } from "@ant-design/icons";
import { Sidebar } from "primereact/sidebar";
import { useFormik } from "formik";
import * as yup from "yup";
import TinyMCEEditor from "../components/QuillEditor";
import { usePageSummary } from "../hooks/usePage";
import { useTeamMember } from "../hooks/useTeam";
import { truncateText } from "../utils/authHelpers";
import { HideSortIcons } from "../utils/tableStyles";
import { useSettings } from "../hooks/useSettings";
const ManageBoardMembers = () => {
  const { settings: headerSettings } = useSettings();
  const communityName = headerSettings?.clubName || "Community";
  const {
    updateTeamPage,
    updateTeamPageLoading,
    teamSummary,
    updateTeamPageSuccess,
  } = usePageSummary();
  const {
    teamMembers,
    isLoading,
    createTeamMemberMutation,
    updateTeamMemberMutation,
    deleteTeamMemberMutation,
  } = useTeamMember();
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [visibleRight, setVisibleRight] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    bio: "",
    company: "",
    community: communityName,
    image: null,
  });
  const [visible, setVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [fileList, setFileList] = useState([]); // Stores the selected image file

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Open dialog for add or edit
  const openDialog = (member = null) => {
    if (member) {
      setEditingId(member.id);
      setFormData({
        fullName: member.fullName || "",
        email: member.email || "",
        phone: member.phone || "",
        bio: member.bio || "",
        company: member.company || "",
        community: communityName,
        image: null,
      });
      setFileList([]);
    } else {
      setEditingId(null);
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        bio: "",
        company: "",
        community: communityName,
        image: null,
      });
      setFileList([]);
    }
    setVisible(true);
  };

  // Reset form on dialog close
  const handleDialogClose = () => {
    setVisible(false);
    setEditingId(null);
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      bio: "",
      company: "",
      community: communityName,
      image: null,
    });
    setFileList([]);
  };

  const handleFileUpload = (info) => {
    setFileList(info.fileList.slice(-1));
    setFormData({
      ...formData,
      image: info.fileList[0]?.originFileObj || null,
    });
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => (
    <div className="flex items-end justify-end">
      <Input
        placeholder="Search"
        className="text-sm focus:ring-0 focus:outline-none"
        value={globalFilterValue}
        onChange={onGlobalFilterChange}
        prefix={<IoIosSearch />}
      />
    </div>
  );

  const saveMember = async () => {
    const form = new FormData();
    form.append("fullName", formData.fullName);
    form.append("email", formData.email);
    form.append("phone", formData.phone);
    form.append("bio", formData.bio);
    form.append("company", formData.company);
    form.append("community", communityName);
    if (formData.image) {
      form.append("image", formData.image);
    }

    const request = editingId
      ? updateTeamMemberMutation.mutateAsync({ id: editingId, formData: form })
      : createTeamMemberMutation.mutateAsync(form);

    request.then(() => {
      setEditingId(null);
      setVisible(false);
      setFileList([]);
    });
  };

  const deleteMember = async (id) => {
    deleteTeamMemberMutation.mutateAsync(id);
  };

  const customHeader = () => (
    <div className="flex items-start justify-start">
      <h1 className="font-semibold">Edit team page content</h1>
    </div>
  );

  const addSummaryFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      summaryText: teamSummary?.description || "",
      instructionText:
        teamSummary?.instructionText ||
        "For more information on the team member, please click on the team member's pictures.",
    },
    validationSchema: yup.object({
      summaryText: yup.string().required("summary is required"),
      instructionText: yup.string().required("instruction text is required"),
    }),
    onSubmit: (values) => {
      updateTeamPage({
        description: values.summaryText,
        instructionText: values.instructionText,
      });
    },
  });

  useEffect(() => {
    if (updateTeamPageSuccess) {
      // Set the value to the latest submitted value, not the stale teamSummary
      addSummaryFormik.setFieldValue(
        "summaryText",
        addSummaryFormik.values.summaryText
      );
      addSummaryFormik.setFieldValue(
        "instructionText",
        addSummaryFormik.values.instructionText
      );
    }
  }, [updateTeamPageSuccess]);

  if (isLoading)
    return (
      <div className="flex h-screen justify-center items-center">
        <i
          className="pi pi-spin pi-spinner text-red-700"
          style={{ fontSize: "2rem" }}
        ></i>
      </div>
    );

  return (
    <div className="p-5">
      <HideSortIcons />
      <div className="flex justify-between">
        <h1 className="lg:text-2xl font-semibold text-[#003049] mb-5">
          Team members management
        </h1>
        <div className="flex gap-5 items-center">
          <Button
            label="Add Member"
            icon="pi pi-plus"
            className="bg-[#BA1500] text-white btn text-[10px] lg:text-sm focus:outline-none focus:ring-0"
            onClick={() => openDialog()}
          />
          <Button
            label="Add summary"
            icon="pi pi-plus"
            className="bg-[#BA1500] text-white btn text-[10px] lg:text-sm focus:outline-none focus:ring-0"
            onClick={() => setVisibleRight(true)}
          />
        </div>
      </div>

      <div className="bg-white rounded-md mt-5 text-[18px]">
        <div className="card">
          <DataTable
            header={renderHeader}
            globalFilter={filters.global.value}
            emptyMessage="No members found."
            filters={filters}
            value={teamMembers}
            paginator
            rows={5}
            sortMode="single"
            showSortIcon={false}
            tableStyle={{ minWidth: "50rem", fontSize: "14px" }}
            className="text-xs sm:text-sm md:text-base"
            onRowDoubleClick={(e) => {
              openDialog(e.data);
            }}
          >
            <Column
              header="Action"
              headerClassName="bg-[#003049] text-white"
              style={{ width: "10%" }}
              body={(rowData) => (
                <div className="flex gap-5">
                  <i
                    className="pi pi-pencil text-blue-600 hover:text-blue-800 cursor-pointer"
                    onClick={() => openDialog(rowData)}
                  />
                </div>
              )}
            />
            <Column
              field="imageUrl"
              header="Image"
              style={{ width: "25%" }}
              body={(rowData) => (
                <img
                  src={rowData.imageUrl}
                  alt={rowData.fullName}
                  className="w-10 h-10 rounded-full"
                />
              )}
              headerClassName="bg-[#003049] text-white"
            />
            <Column
              field="fullName"
              header="Name"
              headerClassName="bg-[#003049] text-white"
              style={{ width: "25%" }}
              sortable
            />
            <Column
              field="community"
              header="Community"
              headerClassName="bg-[#003049] text-white"
              style={{ width: "25%" }}
              body={() => <span title={communityName}>{communityName}</span>}
              sortable
            />
            <Column
              header="Action"
              headerClassName="bg-[#003049] text-white"
              style={{ width: "10%" }}
              body={(rowData) => (
                <div className="flex gap-5">
                  <i
                    className="pi pi-trash text-red-600 hover:text-red-800 cursor-pointer"
                    onClick={() => deleteMember(rowData.id)}
                  />
                </div>
              )}
            />
          </DataTable>
        </div>
      </div>

      <Sidebar
        visible={visibleRight}
        position="right"
        onHide={() => setVisibleRight(false)}
        style={{ width: "30rem" }}
        header={customHeader}
      >
        <div className="mt-5">
          <form onSubmit={addSummaryFormik.handleSubmit}>
            <div className="py-2 my-2">
              <label className="block text-sm font-medium mb-2">
                Instruction text:
              </label>
              <Input.TextArea
                name="instructionText"
                value={addSummaryFormik.values.instructionText}
                onChange={(e) =>
                  addSummaryFormik.setFieldValue(
                    "instructionText",
                    e.target.value
                  )
                }
                onBlur={() =>
                  addSummaryFormik.setFieldTouched("instructionText", true)
                }
                rows={3}
                placeholder="For more information on the team member, please click on the team member's pictures."
                className="w-full"
              />
              {addSummaryFormik.touched.instructionText &&
                addSummaryFormik.errors.instructionText && (
                  <div
                    className="text-[#BA1500] text-sm mt-1"
                    aria-live="polite"
                  >
                    {addSummaryFormik.errors.instructionText}
                  </div>
                )}
            </div>
            <div className="py-2 my-2">
              <label className="block text-sm font-medium mb-2">
                Summary text:
              </label>
              <TinyMCEEditor
                value={addSummaryFormik.values.summaryText}
                onChange={(html) =>
                  addSummaryFormik.setFieldValue("summaryText", html)
                }
                onBlur={() =>
                  addSummaryFormik.setFieldTouched("summaryText", true)
                }
              />

              {addSummaryFormik.touched.summaryText &&
                addSummaryFormik.errors.summaryText && (
                  <div
                    className="text-[#BA1500] text-sm mt-1"
                    aria-live="polite"
                  >
                    {addSummaryFormik.errors.summaryText}
                  </div>
                )}
            </div>
            <button
              type="submit"
              className="w-full bg-[#BA1500] text-center text-white py-3 disabled:opacity-50"
              disabled={updateTeamPageLoading}
            >
              {updateTeamPageLoading ? (
                <i
                  className="pi pi-spin pi-spinner text-white"
                  style={{ fontSize: "2rem" }}
                ></i>
              ) : (
                "save"
              )}
            </button>
          </form>
        </div>
      </Sidebar>

      <Dialog
        header={editingId ? "Edit member" : "Add member"}
        visible={visible}
        className="w-full lg:w-[50%]"
        onHide={handleDialogClose}
      >
        <Form layout="vertical" onFinish={saveMember}>
          {["fullName", "email", "phone", "company"].map((field) => (
            <Form.Item
              label={field.charAt(0).toUpperCase() + field.slice(1)}
              key={field}
            >
              <Input
                name={field}
                value={formData[field] || ""}
                onChange={handleInputChange}
              />
            </Form.Item>
          ))}
          <Form.Item label="Bio">
            <Input.TextArea
              name="bio"
              value={formData.bio || ""}
              onChange={handleInputChange}
              rows={6}
              style={{ minHeight: "120px" }}
            />
          </Form.Item>
          <Form.Item label="Upload image">
            <Upload
              fileList={fileList}
              beforeUpload={() => false}
              onChange={handleFileUpload}
              maxCount={1}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />} type="button">
                Select Image
              </Button>
            </Upload>
          </Form.Item>
          <Button
            htmlType="submit"
            label="Save"
            className="w-full bg-[#BA1500] text-white py-3"
            loading={updateTeamPageLoading}
          />
        </Form>
      </Dialog>
    </div>
  );
};

export default ManageBoardMembers;
